<script setup lang="ts">
import { computed, type Component } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAdminAuthStore, type AdminRole } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import {
  LayoutDashboard,
  Building2,
  Users,
  CreditCard,
  Wallet,
  ScrollText,
  Server,
  Activity,
  Flag,
  ShieldCheck,
  Monitor,
} from 'lucide-vue-next'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SidebarItem {
  label: string
  icon: Component
  route: string
  section: 'main' | 'billing' | 'monitoring' | 'admin_settings'
  phase: number
  requiredRoles?: AdminRole[]
}

// ---------------------------------------------------------------------------
// Current phase — bump as phases are implemented
// ---------------------------------------------------------------------------

const CURRENT_PHASE = 8

// ---------------------------------------------------------------------------
// Navigation items
// ---------------------------------------------------------------------------

const allNavItems: SidebarItem[] = [
  // MAIN section
  {
    label: 'Dashboard',
    icon: LayoutDashboard,
    route: '/admin/dashboard',
    section: 'main',
    phase: 1,
  },
  {
    label: 'Organizations',
    icon: Building2,
    route: '/admin/organizations',
    section: 'main',
    phase: 1,
  },
  {
    label: 'Users',
    icon: Users,
    route: '/admin/users',
    section: 'main',
    phase: 1,
  },
  {
    label: 'Plans',
    icon: CreditCard,
    route: '/admin/plans',
    section: 'main',
    phase: 1,
    requiredRoles: ['super_admin', 'billing'],
  },

  // BILLING section
  {
    label: 'Subscriptions',
    icon: Wallet,
    route: '/admin/subscriptions',
    section: 'billing',
    phase: 8,
    requiredRoles: ['super_admin', 'billing'],
  },

  // MONITORING section
  {
    label: 'Audit Logs',
    icon: ScrollText,
    route: '/admin/audit-logs',
    section: 'monitoring',
    phase: 8,
  },
  {
    label: 'System Health',
    icon: Server,
    route: '/admin/system/health',
    section: 'monitoring',
    phase: 8,
    requiredRoles: ['super_admin'],
  },
  {
    label: 'Platform Health',
    icon: Activity,
    route: '/admin/system/platform-health',
    section: 'monitoring',
    phase: 8,
    requiredRoles: ['super_admin'],
  },
  {
    label: 'Feature Flags',
    icon: Flag,
    route: '/admin/system/feature-flags',
    section: 'monitoring',
    phase: 8,
    requiredRoles: ['super_admin'],
  },

  // ADMIN SETTINGS section
  {
    label: 'Admin Users',
    icon: ShieldCheck,
    route: '/admin/admin-users',
    section: 'admin_settings',
    phase: 1,
    requiredRoles: ['super_admin'],
  },
  {
    label: 'Sessions',
    icon: Monitor,
    route: '/admin/sessions',
    section: 'admin_settings',
    phase: 8,
  },
]

// ---------------------------------------------------------------------------
// Stores & router
// ---------------------------------------------------------------------------

const authStore = useAdminAuthStore()
const uiStore = useUiStore()
const router = useRouter()
const route = useRoute()

// ---------------------------------------------------------------------------
// Computed
// ---------------------------------------------------------------------------

const adminRole = computed<AdminRole | null>(() => authStore.adminUser?.admin_role ?? null)

const isCollapsed = computed(() => uiStore.sidebarCollapsed)

const visibleNavItems = computed(() => {
  const role = adminRole.value
  if (!role) return []

  return allNavItems.filter((item) => {
    if (item.phase > CURRENT_PHASE) return false
    if (item.requiredRoles && !item.requiredRoles.includes(role)) return false
    return true
  })
})

/** Group nav items by section for rendering */
const mainItems = computed(() => visibleNavItems.value.filter((i) => i.section === 'main'))
const billingItems = computed(() => visibleNavItems.value.filter((i) => i.section === 'billing'))
const monitoringItems = computed(() =>
  visibleNavItems.value.filter((i) => i.section === 'monitoring'),
)
const adminSettingsItems = computed(() =>
  visibleNavItems.value.filter((i) => i.section === 'admin_settings'),
)

const sections = computed(() => {
  const result: { key: string; label: string; items: SidebarItem[] }[] = []

  if (mainItems.value.length > 0) {
    result.push({ key: 'main', label: 'Main', items: mainItems.value })
  }
  if (billingItems.value.length > 0) {
    result.push({ key: 'billing', label: 'Billing', items: billingItems.value })
  }
  if (monitoringItems.value.length > 0) {
    result.push({ key: 'monitoring', label: 'Monitoring', items: monitoringItems.value })
  }
  if (adminSettingsItems.value.length > 0) {
    result.push({ key: 'admin_settings', label: 'Admin Settings', items: adminSettingsItems.value })
  }

  return result
})

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isActive(itemRoute: string): boolean {
  return route.path === itemRoute || route.path.startsWith(itemRoute + '/')
}

function navigate(itemRoute: string) {
  router.push(itemRoute)
}

const currentYear = new Date().getFullYear()
</script>

<template>
  <aside
    :class="[
      'fixed top-16 left-0 bottom-0 bg-bg-primary border-r border-border z-[39] flex flex-col transition-all duration-base',
      isCollapsed ? 'w-[70px]' : 'w-[240px]',
    ]"
    data-testid="admin-sidebar"
  >
    <!-- Navigation -->
    <nav class="flex-1 overflow-y-auto admin-scrollbar py-4" aria-label="Admin navigation">
      <div v-for="section in sections" :key="section.key" class="mb-4">
        <!-- Section label (hidden when collapsed) -->
        <div
          v-if="!isCollapsed && sections.length > 1"
          class="px-4 mb-2"
        >
          <span class="text-xs font-semibold text-text-muted uppercase tracking-wider">
            {{ section.label }}
          </span>
        </div>

        <!-- Nav items -->
        <ul class="space-y-1 px-2">
          <li v-for="item in section.items" :key="item.route">
            <button
              :class="[
                'w-full flex items-center gap-3 rounded-md text-sm font-medium transition-colors duration-fast',
                isCollapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2.5',
                isActive(item.route)
                  ? 'bg-bg-secondary text-text-primary'
                  : 'text-text-secondary hover:bg-bg-secondary hover:text-text-primary',
              ]"
              :title="isCollapsed ? item.label : undefined"
              :aria-current="isActive(item.route) ? 'page' : undefined"
              :data-testid="`sidebar-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`"
              @click="navigate(item.route)"
            >
              <component
                :is="item.icon"
                :size="18"
                class="flex-shrink-0"
                aria-hidden="true"
              />
              <span v-if="!isCollapsed" class="truncate">{{ item.label }}</span>
            </button>
          </li>
        </ul>
      </div>

    </nav>

    <!-- Footer: Copyright + Version -->
    <div class="border-t border-border flex-shrink-0">
      <Transition
        enter-active-class="transition-opacity duration-fast"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-fast"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div v-if="!isCollapsed" class="px-4 py-3 text-center">
          <p class="text-[10px] text-text-muted leading-relaxed">
            &copy; {{ currentYear }} Postlytics
          </p>
          <p class="text-[10px] text-text-muted/60">
            v0.1.0
          </p>
        </div>
      </Transition>
      <!-- Collapsed: just show version dot -->
      <div v-if="isCollapsed" class="py-3 text-center">
        <span class="text-[9px] text-text-muted/50">v0.1</span>
      </div>
    </div>
  </aside>
</template>
